/**
 * Requested pieces (« Pièces ») of a contract or a claim — fiches 22721 /
 * 22746: one design-system `Card` per piece with its label, its status badge
 * (« EN ATTENTE » → orange « En attente ») and « Demandé le JJ/MM/AAAA ».
 *
 * Configurable-ready: each piece renders from the declarative descriptors
 * `PIECE_ITEM_FIELDS` (`./pieceItems.ts` — stable keys `libelle`, `statut`,
 * `dateDemande`; labels/tooltips in config). `_layoutOverrides` (`items` /
 * `fields` bucket) renames (the label is the « Demandé le » prefix of
 * `dateDemande`), hides, reorders (within a line) and tooltips them;
 * `setTitle` renames the title. Publishes its base config under
 * `componentId`.
 *
 * Piece upload (fiches 22975 / 22976) is **opt-in per usage**: with an
 * `upload` config bound, every pending piece that carries its upload ids
 * gets a `PieceUploadZone` — on the right half of the card from 768px, below
 * the texts and full width on mobile (`Grid` 1 → 2 columns). The zone is the
 * item `depot` (hide / accessible-name rename / tooltip) and its « Valider »
 * the action `submit` (`setActionLabel`; `hideAction` hides the zone); both
 * are published only when upload is on. Without `upload` (no bound config)
 * the card renders exactly as before.
 *
 * NOT registered by itself — registered per usage
 * (`extranet.contractPiecesCard`, `extranet.claimPiecesCard`).
 */

import { useMemo, type ReactNode } from 'react';
import { Inbox, type LucideIcon } from 'lucide-react';

import { Text, Tooltip } from '@components/base/display';
import { Card, Grid, HStack, VStack } from '@components/base/layout';
import {
  applyItemOverrides,
  buildPublishedConfigFromInfoItems,
  resolveCardTitle,
  usePublishLayoutConfig,
  type LayoutOverrides,
} from '@components/shared/configurable';

import { CardBodyState } from './CardBodyState';
import { CardTitleRow } from './CardTitleRow';
import {
  PIECE_ITEM_FIELDS,
  isPendingPiece,
  pieceFieldText,
  type PieceItem,
  type PieceItemField,
} from './pieceItems';
import {
  PIECE_DEPOT_KEY,
  PIECE_UPLOAD_SUBMIT_ACTION,
  pieceDepotField,
  type PieceUploadConfig,
} from './pieceUploadConfig';
import { PieceUploadZone } from './PieceUploadZone';
import { StatusBadge } from './StatusBadge';
import { PIECE_STATUS_BADGE, type StatusBadgeConfig } from './statusBadgeConfig';

export type { PieceItem } from './pieceItems';

export interface PiecesCardProps {
  /** Registered id of this usage (bound by `bindCardProps`). */
  componentId: string;
  /** Base title (overridable); omitted ⇒ no title row. */
  title?: string;
  icon?: LucideIcon;
  pieces: readonly PieceItem[];
  loading?: boolean;
  error?: string | null;
  /** Status badge map; defaults to `PIECE_STATUS_BADGE`. */
  statusBadge?: StatusBadgeConfig;
  /**
   * Piece upload config (texts + size limit) — bound per usage; omitted ⇒
   * no upload zone.
   */
  upload?: PieceUploadConfig;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

/** The upload zone of the card, resolved against the overrides. */
interface ResolvedDepot {
  config: PieceUploadConfig;
  zoneLabel: string;
  tooltip?: string;
  submitLabel: string;
}

function sortByOrder(fields: PieceItemField[]): PieceItemField[] {
  return fields.sort(
    (a, b) => (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY),
  );
}

export function PiecesCard({
  componentId,
  title,
  icon = Inbox,
  pieces,
  loading = false,
  error = null,
  statusBadge = PIECE_STATUS_BADGE,
  upload,
  _layoutOverrides,
}: Readonly<PiecesCardProps>) {
  usePublishLayoutConfig(componentId, {
    // `title ?? ''` keeps the editor's title slot (InfoList host) without a base title.
    ...buildPublishedConfigFromInfoItems(
      upload ? [...PIECE_ITEM_FIELDS, pieceDepotField(upload)] : [...PIECE_ITEM_FIELDS],
      title ?? '',
    ),
    ...(upload
      ? {
          actions: {
            [PIECE_UPLOAD_SUBMIT_ACTION]: { label: upload.text.submit, variant: 'primary' as const },
          },
        }
      : {}),
  });

  const { primaryFields, secondaryFields } = useMemo(() => {
    const overrides = _layoutOverrides?.items ?? _layoutOverrides?.fields;
    const visible = [...applyItemOverrides(PIECE_ITEM_FIELDS, overrides)].filter(
      (field) => field.visible !== false,
    );
    return {
      primaryFields: sortByOrder(visible.filter((field) => field.line === 'primary')),
      secondaryFields: sortByOrder(visible.filter((field) => field.line === 'secondary')),
    };
  }, [_layoutOverrides]);

  const depot = useMemo<ResolvedDepot | null>(() => {
    if (!upload) return null;
    const overrides = _layoutOverrides?.items ?? _layoutOverrides?.fields;
    const [field] = applyItemOverrides([pieceDepotField(upload)], overrides);
    const submit = _layoutOverrides?.actions?.[PIECE_UPLOAD_SUBMIT_ACTION];
    if (field?.visible === false || submit?.visible === false) return null;
    return {
      config: upload,
      zoneLabel: field?.label ?? upload.text.zoneLabel,
      tooltip: field?.tooltip,
      submitLabel: submit?.label ?? upload.text.submit,
    };
  }, [upload, _layoutOverrides]);

  const resolvedTitle = resolveCardTitle(title ?? '', _layoutOverrides);

  const renderField = (field: PieceItemField, piece: PieceItem, isFirst: boolean) => {
    const isPrimary = field.line === 'primary';
    const content = field.badge ? (
      <StatusBadge value={piece[field.key]} config={statusBadge} />
    ) : (
      <Text
        as="span"
        size={isPrimary ? 'sm' : 'xs'}
        weight={isPrimary && isFirst ? 'medium' : 'normal'}
        color={isPrimary ? 'default' : 'muted'}
      >
        {pieceFieldText(field, piece)}
      </Text>
    );
    return field.tooltip ? (
      <Tooltip key={field.key} content={field.tooltip}>
        <span data-field-key={field.key}>{content}</span>
      </Tooltip>
    ) : (
      <span key={field.key} data-field-key={field.key}>
        {content}
      </span>
    );
  };

  const renderTexts = (piece: PieceItem) => (
    <VStack gap={1}>
      {primaryFields.length > 0 && (
        <HStack gap={2} align="center" wrap>
          {primaryFields.map((field, index) => renderField(field, piece, index === 0))}
        </HStack>
      )}
      {secondaryFields.map((field, index) => renderField(field, piece, index === 0))}
    </VStack>
  );

  const renderDepot = (piece: PieceItem, resolved: ResolvedDepot): ReactNode => {
    const target = piece.upload ?? null;
    if (target === null || !isPendingPiece(piece)) return null;
    const zone = (
      <div data-field-key={PIECE_DEPOT_KEY}>
        <PieceUploadZone
          target={target}
          pieceLabel={piece.libelle}
          config={resolved.config}
          zoneLabel={resolved.zoneLabel}
          submitLabel={resolved.submitLabel}
        />
      </div>
    );
    return resolved.tooltip ? <Tooltip content={resolved.tooltip}>{zone}</Tooltip> : zone;
  };

  return (
    <VStack gap={3} data-testid="pieces-card">
      {resolvedTitle !== '' && <CardTitleRow title={resolvedTitle} icon={icon} />}
      <CardBodyState loading={loading} error={error} empty={pieces.length === 0}>
        {pieces.map((piece) => {
          const zone = depot === null ? null : renderDepot(piece, depot);
          return (
            <Card key={piece.id} padding="sm" data-piece-id={piece.id}>
              <Card.Content>
                {zone === null ? (
                  renderTexts(piece)
                ) : (
                  <Grid columns={{ base: 1, md: 2 }} gap={4} align="center">
                    <Grid.Item>{renderTexts(piece)}</Grid.Item>
                    <Grid.Item>{zone}</Grid.Item>
                  </Grid>
                )}
              </Card.Content>
            </Card>
          );
        })}
      </CardBodyState>
    </VStack>
  );
}
